import React from 'react'
import { Box, Tooltip, Chip } from '@mui/material'
import { styled } from '@mui/material/styles'
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

const StatusContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 8px',
  borderRadius: '6px',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    padding: '3px 6px',
    gap: '4px',
  },
}))

const StatusIndicator = styled(Box)(({ status }) => ({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor:
    status === 'online'
      ? '#10b981'
      : status === 'offline'
      ? '#ef4444'
      : '#f59e0b',
  boxShadow:
    status === 'online'
      ? '0 0 8px rgba(16, 185, 129, 0.6)'
      : status === 'offline'
      ? '0 0 8px rgba(239, 68, 68, 0.6)'
      : '0 0 8px rgba(245, 158, 10, 0.6)',
  animation: status === 'offline' ? 'pulse 2s infinite' : 'none',
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
  },
}))

/**
 * Chamber Room Status Indicator Component
 * Shows whether the chamber room system/app is online or offline
 * 
 * @param {string} status - 'online', 'offline', or 'unknown'
 * @param {string} chamberRoomName - Name of the chamber room (optional)
 * @param {boolean} compact - Show compact version (default: false)
 */
export default function ChamberRoomStatusIndicator({
  status = 'unknown',
  chamberRoomName = null,
  compact = false,
}) {
  const getStatusInfo = () => {
    switch (status) {
      case 'online':
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />,
          text: 'Chamber Room Online',
          color: '#10b981',
          tooltip: chamberRoomName
            ? `${chamberRoomName} is online and connected`
            : 'Chamber room system is online and connected',
        }
      case 'offline':
        return {
          icon: <SignalWifiOffIcon sx={{ fontSize: 14, color: '#ef4444' }} />,
          text: 'Chamber Room Offline',
          color: '#ef4444',
          tooltip: chamberRoomName
            ? `${chamberRoomName} is offline or not responding. Please check if the chamber room system is running.`
            : 'Chamber room system is offline or not responding. Please check if the system is running.',
        }
      default:
        return {
          icon: <ErrorOutlineIcon sx={{ fontSize: 14, color: '#f59e0b' }} />,
          text: 'Status Unknown',
          color: '#f59e0b',
          tooltip: 'Chamber room status is being checked...',
        }
    }
  }

  const statusInfo = getStatusInfo()

  if (compact) {
    return (
      <Tooltip title={statusInfo.tooltip} arrow>
        <StatusContainer>
          <StatusIndicator status={status} />
          {statusInfo.icon}
        </StatusContainer>
      </Tooltip>
    )
  }

  return (
    <Tooltip title={statusInfo.tooltip} arrow>
      <Chip
        icon={statusInfo.icon}
        label={statusInfo.text}
        size="small"
        sx={{
          height: '24px',
          fontSize: '11px',
          fontWeight: 500,
          backgroundColor:
            status === 'online'
              ? 'rgba(16, 185, 129, 0.1)'
              : status === 'offline'
              ? 'rgba(239, 68, 68, 0.1)'
              : 'rgba(245, 158, 10, 0.1)',
          color: statusInfo.color,
          border: `1px solid ${statusInfo.color}40`,
          '& .MuiChip-icon': {
            color: statusInfo.color,
            fontSize: '14px',
          },
          '&:hover': {
            backgroundColor:
              status === 'online'
                ? 'rgba(16, 185, 129, 0.15)'
                : status === 'offline'
                ? 'rgba(239, 68, 68, 0.15)'
                : 'rgba(245, 158, 10, 0.15)',
          },
        }}
      />
    </Tooltip>
  )
}






